'use client';

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "../../utils/cn";

/**
 * Button — MV5 Figma ComponentSet mirror.
 *
 * Variants:
 *   primary     — Hauraki violet fill (accent), on-violet white text.
 *   secondary   — Neutral panel fill, ink-primary text, chrome border.
 *   destructive — Status-error fill for negative actions.
 *   icon        — Square icon-only button; use `size` for footprint.
 *   ghost       — Transparent, hover panel — for compact inline actions.
 *
 * Sizes:
 *   L — 48px tall, body-16 label. Hero + primary form actions.
 *   M — 40px tall, body-14 label. Default.
 *   S — 32px tall, caption-12 label. Toolbars + inline.
 *
 * States:
 *   default / hover / focus-visible / disabled / loading.
 *   `loading` renders a spinner and disables interaction without changing width
 *   (the label collapses via visibility, not display, to preserve layout).
 *
 * Reference: Figma ComponentSet Button/Primary (node 144:269).
 */
const buttonVariants = cva(
  [
    "relative inline-flex items-center justify-center gap-space-8",
    "whitespace-nowrap font-sans font-medium",
    "rounded-card border border-transparent",
    "transition-colors duration-micro ease-standard",
    "outline-none focus-visible:ring-2 focus-visible:ring-accent-violet focus-visible:ring-offset-2 focus-visible:ring-offset-chrome-surface",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "[&>svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        primary:
          "bg-accent-violet text-white hover:bg-accent-violet-hover disabled:hover:bg-accent-violet",
        secondary:
          "bg-chrome-panel text-ink-primary border-chrome-border hover:bg-chrome-border/50 disabled:hover:bg-chrome-panel",
        destructive:
          "bg-status-error text-white hover:brightness-110 disabled:hover:brightness-100",
        icon:
          "bg-transparent text-ink-primary hover:bg-chrome-panel disabled:hover:bg-transparent",
        ghost:
          "bg-transparent text-ink-primary hover:bg-chrome-panel disabled:hover:bg-transparent",
      },
      size: {
        L: "h-[48px] px-space-24 text-body-16 [&>svg]:size-5",
        M: "h-[40px] px-space-16 text-body-14 [&>svg]:size-4",
        S: "h-[32px] px-space-12 text-caption-12 [&>svg]:size-4",
      },
    },
    compoundVariants: [
      { variant: "icon", size: "L", className: "px-0 w-[48px]" },
      { variant: "icon", size: "M", className: "px-0 w-[40px]" },
      { variant: "icon", size: "S", className: "px-0 w-[32px]" },
    ],
    defaultVariants: {
      variant: "primary",
      size: "M",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as the child element (Radix Slot pattern). */
  asChild?: boolean;
  /** Show a loading spinner and disable interaction. */
  loading?: boolean;
  /** Optional icon slot rendered before the label. */
  leftIcon?: React.ReactNode;
  /** Optional icon slot rendered after the label. */
  rightIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      disabled,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref,
  ) {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        data-slot="button"
        data-loading={loading || undefined}
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && (
          <span
            className="absolute inset-0 flex items-center justify-center"
            aria-hidden="true"
          >
            <Loader2 className="animate-spin" />
          </span>
        )}
        <span
          className={cn(
            "inline-flex items-center gap-space-8",
            loading && "invisible",
          )}
        >
          {leftIcon}
          {children}
          {rightIcon}
        </span>
      </Comp>
    );
  },
);

Button.displayName = "Button";

export { buttonVariants };
